import { useCallback, useEffect, useRef, useState } from 'react';
import { AppBar, Box, Stack, Toolbar, Typography } from '@mui/material';
import Link from '@mui/material/Link';
import { styled, keyframes, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link as RouterLink } from 'react-router-dom';
import DashboardRoundedIcon    from '@mui/icons-material/DashboardRounded';
import LoginRoundedIcon        from '@mui/icons-material/LoginRounded';
import WifiRoundedIcon         from '@mui/icons-material/WifiRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import AuthService from '../../services/authService';
import AuthenticatedContent from '../../common/AuthenticatedContent';
import { useActiveTickets, WS_STATUS } from '../activeTickets/useActiveTickets';

const HIDE_DELAY          = 1800;
const ROWS_DESKTOP        = 8;
const ROWS_MOBILE         = 5;
const HIGHLIGHT_DURATION  = 8000;
const MAX_DISPLAY_TICKETS = 16;
const MAX_WAITING_DISPLAY = 12;

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useClock() {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    return now;
}

function useAutoFullscreen(enabled) {
    const [isFullscreen, setIsFullscreen] = useState(() => !!document.fullscreenElement);
    useEffect(() => {
        const onChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onChange);
        return () => document.removeEventListener('fullscreenchange', onChange);
    }, []);
    const enter = useCallback(() => {
        if (!document.fullscreenElement)
            document.documentElement.requestFullscreen().catch(() => {});
    }, []);
    useEffect(() => {
        if (!enabled || isFullscreen) return;
        const handler = () => enter();
        window.addEventListener('click',      handler, { once: true });
        window.addEventListener('keydown',    handler, { once: true });
        window.addEventListener('touchstart', handler, { once: true, passive: true });
        return () => {
            window.removeEventListener('click',      handler);
            window.removeEventListener('keydown',    handler);
            window.removeEventListener('touchstart', handler);
        };
    }, [enabled, isFullscreen, enter]);
}

// ─── Строка (пилюля) ──────────────────────────────────────────────────────────

function QueueRow({ ticket, highlighted }) {
    if (!ticket) return <EmptyPill />;
    const winLabel = ticket.windowNumber ? String(ticket.windowNumber).padStart(2, '0') : '—';
    return (
        <TicketPill $highlighted={highlighted}>
            <Typography sx={{
                fontWeight: 800,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.6rem', lg: '3.2rem' },
                fontFamily: '"Roboto Mono", monospace',
                letterSpacing: '0.05em',
                lineHeight: 1,
                color: 'inherit',
            }}>
                {ticket.ticketNumber}
            </Typography>
            <Typography sx={{
                fontWeight: 300,
                fontSize: { xs: '1.2rem', sm: '1.6rem', md: '2.1rem', lg: '2.7rem' },
                lineHeight: 1,
                color: 'inherit',
                opacity: 0.85,
                mx: { xs: 2, sm: 3, md: 4 },
                fontFamily: 'sans-serif',
            }}>
                ›
            </Typography>
            <Typography sx={{
                fontWeight: 800,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.6rem', lg: '3.2rem' },
                fontFamily: '"Roboto Mono", monospace',
                letterSpacing: '0.05em',
                lineHeight: 1,
                color: 'inherit',
            }}>
                {winLabel}
            </Typography>
        </TicketPill>
    );
}

// ─── Панель ───────────────────────────────────────────────────────────────────

function QueuePanel({ tickets, rows, highlightedNums }) {
    const padded = [...tickets];
    while (padded.length < rows) padded.push(null);
    const visible = padded.slice(0, rows);

    return (
        <PanelContainer>
            <PanelHeaderRow>
                <PillHeader>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.6rem', sm: '0.72rem', md: '0.88rem', lg: '1rem' }, letterSpacing: '0.08em', lineHeight: 1.3 }}>
                        НӨМІР
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.6rem', sm: '0.72rem', md: '0.88rem', lg: '1rem' }, letterSpacing: '0.08em', lineHeight: 1.3 }}>
                        НОМЕР
                    </Typography>
                </PillHeader>
                <PillHeader>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.6rem', sm: '0.72rem', md: '0.88rem', lg: '1rem' }, letterSpacing: '0.08em', lineHeight: 1.3 }}>
                        ТЕРЕЗЕ
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.6rem', sm: '0.72rem', md: '0.88rem', lg: '1rem' }, letterSpacing: '0.08em', lineHeight: 1.3 }}>
                        ОКНО
                    </Typography>
                </PillHeader>
            </PanelHeaderRow>
            <PanelTicketList>
                {visible.map((t, i) => (
                    <QueueRow
                        key={t ? t.ticketNumber : `empty-${i}`}
                        ticket={t}
                        highlighted={t ? highlightedNums.has(t.ticketNumber) : false}
                    />
                ))}
            </PanelTicketList>
        </PanelContainer>
    );
}

// ─── Блок ожидающих ───────────────────────────────────────────────────────────

function WaitingBlock({ tickets }) {
    const limited = tickets.slice(0, MAX_WAITING_DISPLAY);
    const extra   = tickets.length - MAX_WAITING_DISPLAY;

    return (
        <TickerBar>
            <TickerLabel>
                <HourglassEmptyRoundedIcon sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, opacity: 0.75 }} />
                <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.7rem', md: '0.85rem' }, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    В ожидании
                </Typography>
                <TickerBadge>{tickets.length}</TickerBadge>
            </TickerLabel>
            <TickerSep />
            <TickerTrack>
                {tickets.length === 0 ? (
                    <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: { xs: '0.8rem', md: '0.95rem' }, fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                        Нет ожидающих
                    </Typography>
                ) : (
                    <>
                        {limited.map((t, i) => (
                            <TickerChip key={`${t.ticketNumber}-${i}`}>{t.ticketNumber}</TickerChip>
                        ))}
                        {extra > 0 && (
                            <TickerChip sx={{ opacity: 0.55, fontStyle: 'italic' }}>+{extra}</TickerChip>
                        )}
                    </>
                )}
            </TickerTrack>
        </TickerBar>
    );
}

// ─── Главный компонент ────────────────────────────────────────────────────────

export const Head3 = () => {
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const now                            = useClock();
    const { tickets, wsStatus, fetched } = useActiveTickets();

    useAutoFullscreen(!isMobile);

    useEffect(() => {
        console.info('[Head3] WebSocket status:', wsStatus);
    }, [wsStatus]);

    const timeStr = now.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    const dateStr = `${String(now.getDate()).padStart(2,'0')}.${String(now.getMonth()+1).padStart(2,'0')}.${String(now.getFullYear()).slice(-2)}`;

    const [headerVisible, setHeaderVisible] = useState(false);
    const hideTimer  = useRef(null);
    const showHeader   = () => { clearTimeout(hideTimer.current); setHeaderVisible(true); };
    const scheduleHide = () => { hideTimer.current = setTimeout(() => setHeaderVisible(false), HIDE_DELAY); };
    useEffect(() => () => clearTimeout(hideTimer.current), []);

    const sortDesc = (a, b) => {
        const da = a.lastModifiedDate || a.createdDate;
        const db = b.lastModifiedDate || b.createdDate;
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return new Date(db) - new Date(da);
    };

    const calledTickets  = (tickets || []).filter(t => String(t.ticketStatus).toUpperCase() === 'CALLED').sort(sortDesc);
    const waitingTickets = (tickets || [])
        .filter(t => String(t.ticketStatus).toUpperCase() === 'WAITING')
        .sort((a, b) => {
            const da = a.createdDate, db = b.createdDate;
            if (!da && !db) return 0;
            if (!da) return 1;
            if (!db) return -1;
            return new Date(db) - new Date(da);
        });

    const rows = isMobile ? ROWS_MOBILE : ROWS_DESKTOP;

    // История вызванных талонов — не удаляем при смене статуса, макс 16
    const [displayHistory, setDisplayHistory]   = useState([]);
    const [highlightedNums, setHighlightedNums] = useState(() => new Set());
    const highlightTimersRef = useRef({});
    const prevCalledRef      = useRef([]);
    const initializedRef     = useRef(false);

    useEffect(() => {
        if (!fetched) return;
        if (!initializedRef.current) {
            setDisplayHistory(calledTickets.slice(0, MAX_DISPLAY_TICKETS));
            prevCalledRef.current = calledTickets;
            initializedRef.current = true;
            return;
        }
        const prevNums    = new Set(prevCalledRef.current.map(t => t.ticketNumber));
        const newlyCalled = calledTickets.filter(t => !prevNums.has(t.ticketNumber));

        if (newlyCalled.length > 0) {
            setDisplayHistory(prev => {
                const freshMap = new Map(calledTickets.map(t => [t.ticketNumber, t]));
                const updated  = prev.map(t => freshMap.get(t.ticketNumber) || t);
                return [...newlyCalled, ...updated].slice(0, MAX_DISPLAY_TICKETS);
            });
            newlyCalled.forEach(t => {
                const num = t.ticketNumber;
                setHighlightedNums(prev => new Set([...prev, num]));
                clearTimeout(highlightTimersRef.current[num]);
                highlightTimersRef.current[num] = setTimeout(() => {
                    setHighlightedNums(prev => { const s = new Set(prev); s.delete(num); return s; });
                    delete highlightTimersRef.current[num];
                }, HIGHLIGHT_DURATION);
            });
        }
        prevCalledRef.current = calledTickets;
    }, [calledTickets, fetched]); // eslint-disable-line react-hooks/exhaustive-deps

    const leftPanelTickets  = displayHistory.slice(0, rows);
    const rightPanelTickets = isMobile ? [] : displayHistory.slice(rows, rows * 2);

    useEffect(() => () => Object.values(highlightTimersRef.current).forEach(clearTimeout), []);

    return (
        <DarkPage>
            {/* ── Скрытый AppBar ──────────────────────────────────────── */}
            <Box onMouseEnter={showHeader} sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 18, zIndex: 1200 }} />
            <AppBar
                position='fixed'
                elevation={0}
                onMouseEnter={showHeader}
                onMouseLeave={scheduleHide}
                sx={{
                    background:     'rgba(18, 85, 90, 0.97)',
                    backdropFilter: 'blur(12px)',
                    borderBottom:   '1px solid rgba(255,255,255,0.15)',
                    zIndex:         1201,
                    transform:      headerVisible ? 'translateY(0)' : 'translateY(-100%)',
                    transition:     'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
            >
                <Toolbar sx={{ px: '1.5rem', minHeight: '3.5rem !important', justifyContent: 'space-between' }}>
                    <Stack direction='row' alignItems='center' spacing='0.75rem'>
                        <BrandLogo src='/logo-white.png' alt='Dulaty University' />
                    </Stack>
                    <Stack direction='row' spacing='0.25rem' alignItems='center'>
                        <WifiRoundedIcon sx={{ color: wsStatus === WS_STATUS.CONNECTED ? '#4ade80' : '#9ca3af', fontSize: '1.4rem', mr: 1 }} />
                        {!AuthService.isLoggedIn() && (
                            <NavLink component={RouterLink} to='/login' underline='none' color='inherit'>
                                <LoginRoundedIcon sx={{ fontSize: '1rem' }} />Войти
                            </NavLink>
                        )}
                        {AuthService.isLoggedIn() && (
                            <>
                                <AuthenticatedContent roles={['ADMIN', 'MODERATOR']}>
                                    <NavLink component={RouterLink} to='/admin' underline='none' color='inherit'>
                                        <DashboardRoundedIcon sx={{ fontSize: '1rem' }} />Администратор
                                    </NavLink>
                                </AuthenticatedContent>
                                <AuthenticatedContent roles={['MANAGER']}>
                                    <NavLink component={RouterLink} to='/manager' underline='none' color='inherit'>
                                        <LoginRoundedIcon sx={{ fontSize: '1rem' }} />Менеджер
                                    </NavLink>
                                </AuthenticatedContent>
                                <NavLink component={RouterLink} onClick={AuthService.logout} underline='none' color='inherit'>
                                    Выход
                                </NavLink>
                            </>
                        )}
                    </Stack>
                </Toolbar>
            </AppBar>

            {/* ── Основной контент ────────────────────────────────────── */}
            <PageLayout>
                {/* ── Заголовок: время / дата + статус ───────────────── */}
                <TitleBar>
                    <Typography sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.85rem', sm: '1.1rem', md: '1.4rem', lg: '1.7rem' },
                        color: 'rgba(255,255,255,0.85)',
                        fontFamily: '"Roboto Mono", monospace',
                        letterSpacing: '0.04em',
                    }}>
                        {timeStr} / {dateStr}
                    </Typography>
                    <LogoImg src='/logo-white.png' alt='Dulaty University' sx={{ height: { xs: 28, sm: 36, md: 48 } }} />
                </TitleBar>

                {/* ── Панели ──────────────────────────────────────────── */}
                <PanelsRow>
                    <PanelWrapper>
                        <QueuePanel tickets={leftPanelTickets} rows={rows} highlightedNums={highlightedNums} />
                    </PanelWrapper>
                    {!isMobile && (
                        <>
                            <PanelDivider />
                            <PanelWrapper>
                                <QueuePanel tickets={rightPanelTickets} rows={rows} highlightedNums={highlightedNums} />
                            </PanelWrapper>
                        </>
                    )}
                </PanelsRow>

                {/* ── Блок ожидающих ──────────────────────────────────── */}
                <WaitingBlock tickets={waitingTickets} />
            </PageLayout>
        </DarkPage>
    );
};

// ─── Keyframes ────────────────────────────────────────────────────────────────

const pulseGreen = keyframes`
    0%   { background: #4ade80; }
    50%  { background: #22c55e; }
    100% { background: #4ade80; }
`;

// ─── Styled components ────────────────────────────────────────────────────────

const DarkPage = styled(Box)(() => ({
    minHeight: '100dvh',
    background: '#3CA8AB',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
    color: '#fff',
}));

const PageLayout = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5, 2.5),
    height: '100dvh',
    overflow: 'hidden',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1.5, 2),
    },
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1, 1.5),
        gap: theme.spacing(0.75),
    },
}));

const TitleBar = styled(Box)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
}));

const PanelsRow = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    minHeight: 0,
    overflow: 'hidden',
    gap: theme.spacing(1.5),
}));

const PanelWrapper = styled(Box)(() => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: 0,
}));

const PanelDivider = styled(Box)(() => ({
    width: 2,
    background: 'rgba(255,255,255,0.30)',
    flexShrink: 0,
    borderRadius: 1,
}));

// ─── Pill-based panel ─────────────────────────────────────────────────────────

const PanelContainer = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    gap: theme.spacing(0.75),
    [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(0.5),
    },
}));

const PanelHeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.75),
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(0.5),
    },
}));

const PillHeader = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 80, 88, 0.40)',
    borderRadius: 30,
    padding: '7px 8px',
    color: 'rgba(255,255,255,0.92)',
    border: '1px solid rgba(255,255,255,0.28)',
    [theme.breakpoints.down('sm')]: {
        borderRadius: 18,
        padding: '5px 6px',
    },
}));

const PanelTicketList = styled(Box)(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    overflow: 'hidden',
    minHeight: 0,
    [theme.breakpoints.down('sm')]: {
        gap: theme.spacing(0.5),
    },
}));

const TicketPill = styled(Box)(({ $highlighted }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    background: $highlighted ? '#4ade80' : 'rgba(0, 85, 95, 0.45)',
    borderRadius: 14,
    color: $highlighted ? '#082b10' : '#fff',
    border: $highlighted ? 'none' : '1px solid rgba(255,255,255,0.22)',
    animation: $highlighted ? `${pulseGreen} 1.5s ease-in-out infinite` : 'none',
    transition: 'background 0.5s ease, color 0.5s ease',
    minHeight: 0,
}));

const EmptyPill = styled(Box)(() => ({
    flex: 1,
    borderRadius: 14,
    background: 'rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.10)',
    minHeight: 0,
}));

const LogoImg = styled('img')(() => ({
    width: 'auto',
    objectFit: 'contain',
    flexShrink: 0,
}));

const BrandLogo = styled('img')(() => ({
    height: '2rem',
    width: 'auto',
    objectFit: 'contain',
}));

const NavLink = styled(Link)(() => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    fontWeight: 700,
    fontSize: '0.8rem',
    padding: '0.35rem 0.6rem',
    borderRadius: '0.5rem',
    color: '#fff',
    transition: 'background-color .2s ease',
    '&:hover': { backgroundColor: 'rgba(255,255,255,.15)' },
}));

// ─── Блок ожидающих ──────────────────────────────────────────────────────────

const TickerBar = styled(Box)(({ theme }) => ({
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    height: 52,
    background: 'rgba(0, 65, 72, 0.40)',
    borderRadius: 12,
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.20)',
    [theme.breakpoints.down('sm')]: {
        height: 44,
        borderRadius: 8,
    },
}));

const TickerLabel = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '0 14px',
    flexShrink: 0,
    color: 'rgba(255,255,255,0.70)',
    [theme.breakpoints.down('sm')]: {
        padding: '0 10px',
        gap: 4,
    },
}));

const TickerBadge = styled(Box)(({ theme }) => ({
    background: 'rgba(255,255,255,0.18)',
    borderRadius: 20,
    padding: '1px 8px',
    fontSize: '0.75rem',
    fontWeight: 800,
    color: '#fff',
    minWidth: 24,
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
        padding: '1px 6px',
        fontSize: '0.7rem',
    },
}));

const TickerSep = styled(Box)(() => ({
    width: 1,
    alignSelf: 'stretch',
    background: 'rgba(255,255,255,0.15)',
    flexShrink: 0,
}));

const TickerTrack = styled(Box)(() => ({
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '0 14px',
}));

const TickerChip = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0, 80, 88, 0.45)',
    border: '1px solid rgba(255,255,255,0.22)',
    borderRadius: 8,
    padding: '3px 12px',
    fontWeight: 700,
    fontSize: '0.9rem',
    color: '#fff',
    fontFamily: '"Roboto Mono", monospace',
    letterSpacing: '0.04em',
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
        fontSize: '0.78rem',
        padding: '2px 8px',
        borderRadius: 6,
    },
}));

export default Head3;
